import { CheckOutlined, DeleteOutlined, FormOutlined } from '@ant-design/icons';
import { Button, Row, Space } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import EditableNorm from 'app/components/EditableNorm/EditableNorm';
import NumberInput from 'app/components/NumberInput/NumberInput';
import { StyledTable } from 'app/components/StyledTable/StyledTable';
import { selectSelectedFieldsTotalArea } from 'app/containers/Map/slice/selectors';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toFixedIfFloat } from 'utils/helpers';
import useGetTaskResourceOptions from 'utils/hooks/useGetTaskResourceOptions';
import { useTaskSlice } from '../../slice';
import { findResourceById } from '../../slice/helpers';
import { selectTaskResourcesState } from '../../slice/selectors';
import { ITaskPlannedResource } from '../../slice/types';
import SeedCultureWarning from '../SeedCultureWarning/SeedCultureWarning';

interface IProps {
  loading: boolean;
  isEditable?: boolean;
  isPrice?: boolean;
}

const TaskPlannedResourcesTable: FC<IProps> = ({
  loading,
  isEditable = false,
  isPrice,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { actions } = useTaskSlice();

  const totalAreaInMeters = useSelector(selectSelectedFieldsTotalArea);
  const totalAreaInHa = totalAreaInMeters / 10000;

  const {
    plannedResources: { updatedResourcesArr },
  } = useSelector(selectTaskResourcesState);

  const [activeNormInputResourceId, setActiveNormInputResourceId] = useState<
    number | null
  >(null);

  const [activePriceInputResourceId, setActivePriceInputResourceId] = useState<
    number | null
  >(null);

  const { allResources } = useGetTaskResourceOptions();
  const [mappedResources, setMappedResources] = useState<
    ITaskPlannedResource[]
  >([]);

  useEffect(() => {
    if (allResources.length) {
      const result: ITaskPlannedResource[] = updatedResourcesArr.map(
        resource => ({
          ...resource,
          globalResource: findResourceById({
            resourceId: resource.resourceId,
            allResources,
          }),
        }),
      );
      setMappedResources(result);
    }
  }, [updatedResourcesArr, allResources]);

  const columns: ColumnsType<ITaskPlannedResource> = [
    {
      title: t('Task.ResourcesCard.Name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      render: (_: unknown, { name, cultureId, cultureName }) => {
        // seed with culture and variation
        if (cultureName && cultureId) {
          return (
            <Row>
              {`${cultureName} - ${name}`}
              <SeedCultureWarning seedCultureId={cultureId} />
            </Row>
          );
        }
        return name;
      },
      sorter: {
        compare: (a, b) => {
          return a.name.localeCompare(b.name);
        },
      },
    },

    {
      title: t('Task.ResourcesCard.RecommendedNorm', 'Rec. Norm'),
      dataIndex: 'recommendedNorm',
      key: 'recommendedNorm',
      render: (_: unknown, { recommendedNorm, globalResource }) =>
        `${recommendedNorm} ${globalResource?.type.unit}/Ha`,
      width: '15%',
      sorter: {
        compare: (a, b) => {
          return a.recommendedNorm - b.recommendedNorm;
        },
      },
    },
    {
      title: t('Task.ResourcesCard.Norm', 'Norm'),
      dataIndex: 'norm',
      key: 'norm',
      render: (_: unknown, { norm, resourceId, globalResource }) => {
        return (
          <EditableNorm
            activeNormInputResourceId={activeNormInputResourceId}
            norm={norm}
            resourceId={resourceId}
            unit={globalResource?.type.unit}
            onChange={(updatedNorm: number) => {
              dispatch(
                actions.updateTaskPlannedResourceNorm({
                  resourceId: resourceId,
                  norm: updatedNorm,
                }),
              );
            }}
            onOpen={() => setActiveNormInputResourceId(resourceId)}
            onClose={() => setActiveNormInputResourceId(null)}
            isEditable={isEditable}
          />
        );
      },
      width: '20%',
      sorter: {
        compare: (a, b) => {
          return a.norm - b.norm;
        },
      },
    },

    {
      title: t('Task.ResourcesCard.Amount', 'Amount'),
      key: 'amount',
      render: (_: unknown, { norm }) => {
        return toFixedIfFloat(norm * totalAreaInHa);
      },
    },

    ...(isPrice
      ? [
          {
            title: t('Task.ResourcesCard.UnitPrice', 'Unit price'),
            dataIndex: 'unitPrice',
            render: (
              _: unknown,
              { resourceId, price }: ITaskPlannedResource,
            ) => {
              if (activePriceInputResourceId === resourceId) {
                return (
                  <NumberInput
                    style={{ width: 120 }}
                    value={price}
                    onChange={e =>
                      dispatch(
                        actions.updateTaskPlannedResourcePrice({
                          resourceId: resourceId,
                          price: e.target.value
                            ? Number(e.target.value)
                            : undefined,
                        }),
                      )
                    }
                    suffix={
                      <Button
                        type="text"
                        onClick={() => setActivePriceInputResourceId(null)}
                      >
                        <CheckOutlined />
                      </Button>
                    }
                  />
                );
              }
              return (
                <Space>
                  <div>{price ?? '-'}</div>
                  {isEditable && (
                    <Button
                      type="text"
                      onClick={() => setActivePriceInputResourceId(resourceId)}
                    >
                      <FormOutlined />
                    </Button>
                  )}
                </Space>
              );
            },
          },

          {
            title: t('Task.ResourcesCard.HaPrice', 'Ha price'),
            dataIndex: 'haPrice',
            render: (_: unknown, { price, norm }: ITaskPlannedResource) => {
              return toFixedIfFloat((price ?? 0) * norm);
            },
          },
        ]
      : []),

    ...(isEditable
      ? [
          {
            title: '',
            dataIndex: 'deleteResource',
            key: 'deleteResource',
            render: (_: unknown, resource: ITaskPlannedResource) => (
              <Button
                onClick={() =>
                  dispatch(
                    actions.removeTaskPlannedResource(resource.resourceId),
                  )
                }
                type="text"
              >
                <DeleteOutlined />
              </Button>
            ),
            width: '46px',
          },
        ]
      : []),
  ];

  const allResourcesPricePerHa = updatedResourcesArr.reduce(
    (accumulator, resource) => {
      const pricePerHa = toFixedIfFloat((resource.price ?? 0) * resource.norm);
      return accumulator + pricePerHa;
    },
    0,
  );

  const getFooter = isPrice
    ? () => {
        return (
          <Row justify="end">
            {t('Task.ResourceCard.PriceAllResourcesHa', {
              defaultValue: 'Price of all resources Ha - {{price}}',
              price: allResourcesPricePerHa,
            })}
          </Row>
        );
      }
    : undefined;

  return (
    <StyledTable
      loading={loading}
      bordered={true}
      columns={columns}
      rowClassName="resource-row"
      dataSource={mappedResources}
      pagination={false}
      footer={getFooter}
    />
  );
};

export default TaskPlannedResourcesTable;
